import React from 'react';
import * as mobx from 'mobx';
import { FormattedMessage, IntlShape } from 'react-intl';
import * as IoC from 'typescript-ioc';
import { IErrorParsing } from '../../../infrastructure/error-parsing';
import { alertServerError, showNotification } from '../../global/modal/alerts-host/alert-templates';
import { IAlertsService } from '../../../infrastructure/alerts';
import { RolesAction } from '../../../api/roles.dao';
import { Space } from '../../../constants/html-character-entities';
import { debounceExecution } from '../../../infrastructure/utils/debounce.utils';
import { RoomFormVM } from '../../_events/_manage-event/_rooms/_room-form/room-form.viewmodel';
import { SEARCH_TERM_MIN_LENGTH } from '../../../constants/common.constants';
import { IUserDetailsDTO, IUsersDAO } from '../../../api/users.dao';
import { IUserGroupDTO, IUserGroupsDAO } from '../../../api/user-groups.dao';
import { UserGroupsFormDataVM } from './user-groups-form-data.viewmodel';
import TrackingServiceProvider from '../../../tracking-service/tracking-service-provider';
import { convertBoolTypeForMixPanel, TRACKING_EVENTS } from '../../../tracking-service/tracking-values';
import { errorText } from '../../../constants/common-labels.constants';

class UserGroupsFormVM {
    @mobx.observable
    public usersList: IUserDetailsDTO[];
    @mobx.observable
    public isSaving: boolean;

    @mobx.observable
    public isLoading: boolean;

    @mobx.observable
    public groupFormDataVM: UserGroupsFormDataVM;

    @mobx.observable
    public userGroupId: number;

    constructor(
        private accountId: number,
        private fetchUserGroupsList?: () => void,
        private closeEditGroupForm?: (userGroupId: number) => void,
        @IoC.Inject private readonly errorParsingService: IErrorParsing = IoC.Container.get(IErrorParsing),
        @IoC.Inject private readonly alertsService: IAlertsService = IoC.Container.get(IAlertsService),
        @IoC.Inject private readonly usersDAO: IUsersDAO = IoC.Container.get(IUsersDAO),
        @IoC.Inject private readonly userGroupsDAO: IUserGroupsDAO = IoC.Container.get(IUserGroupsDAO),
        @IoC.Inject private readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider),
    ) {
        this.groupFormDataVM = new UserGroupsFormDataVM();
        this.isLoading = true;
        this.fetchUsersList();
    }

    public getErrorMessage(intl: IntlShape): string | null {
        const { showValidationErrors, getNameError, getDescriptionError, serverErrorMessage } = this.groupFormDataVM;
        if (showValidationErrors()) {
            if (getNameError(intl)) {
                return getNameError(intl);
            } else if (getDescriptionError(intl)) {
                return getDescriptionError(intl);
            }
        }
        return serverErrorMessage;
    }

    public initGroup = async (userGroupId?: number) => {
        try {
            if (userGroupId) {
                const groupDetails = await this.userGroupsDAO.getUserGroup({
                    userGroupId: userGroupId,
                    accountId: this.accountId
                });
                mobx.runInAction(() => {
                    this.userGroupId = userGroupId;
                });
                this.handleGroupLoaded(groupDetails);
            }
        } catch (e) {
            if (userGroupId) this.closeEditGroupForm(userGroupId);
            alertServerError(errorText(), this.errorParsingService.extractMessage(e));
        } finally {
            mobx.runInAction(() => {
                this.isSaving = false;
                this.isLoading = false;
            });
        }
    };

    @mobx.action
    private handleGroupLoaded = (groupDetails: IUserGroupDTO) => {
        this.groupFormDataVM = new UserGroupsFormDataVM(groupDetails);
    };

    @mobx.action
    public handleSubmit = async (intl: IntlShape, roleAction: string) => {
        this.groupFormDataVM.serverErrorMessage = '';
        const isFormInvalid = !this.groupFormDataVM.isFormValid(intl);
        if (isFormInvalid) {
            this.groupFormDataVM.allowToShowValidationErrors();
            return false;
        }
        try {
            this.isSaving = true;
            if (roleAction === RolesAction.Create) {
                const params = {
                    accountId: this.accountId,
                    ...this.groupFormDataVM.toApiEditGroupDTO()
                };
                await this.userGroupsDAO.createGroup(params);
                this.trackingService.get().track(TRACKING_EVENTS.NEW_USER_GROUP, {
                    'Name': params.name,
                    'Description': convertBoolTypeForMixPanel(params.description !== this.groupFormDataVM.initValues.description),
                    'Number of users in the group': params.users.length
                });
                showNotification(
                    intl.formatMessage({
                        id: 'oe.userGroups.groupAdded',
                        description: 'Notification about added group',
                        defaultMessage: 'New group added'
                    })
                );
            } else {
                const params = {
                    accountId: this.accountId,
                    userGroupId: this.userGroupId,
                    ...this.groupFormDataVM.toApiEditGroupDTO()
                };
                await this.userGroupsDAO.editGroup(params);
                this.trackingService.get().track(TRACKING_EVENTS.EDIT_USER_GROUP, {
                    'Name': params.name,
                    'Description': convertBoolTypeForMixPanel(params.description !== this.groupFormDataVM.initValues.description),
                    'Number of users in the group before change': this.groupFormDataVM.initValues.users.length,
                    'Number of users in the group after change': params.users.length
                });
                showNotification(
                    intl.formatMessage({
                        id: 'oe.userGroups.groupUpdated',
                        description: 'Notification about group update',
                        defaultMessage: 'Group updated'
                    })
                );
            }
            this.fetchUserGroupsList();
            return true;
        } catch (error) {
            this.onCreateGroupError(error);
            return false;
        } finally {
            mobx.runInAction(() => {
                this.isSaving = false;
            });
        }
    };

    @mobx.action
    private onCreateGroupError(error): void {
        const errorMessage = this.errorParsingService.extractMessage(error);
        if (errorMessage) {
            this.groupFormDataVM.serverErrorMessage = errorMessage;
        }
    }

    @mobx.action
    public onDeleteGroup = () => {
        this.alertsService.confirm({
            message: (
                <span className='alert-message'>
                    <FormattedMessage
                        id='oe.confirm.areYouSureTo'
                        description='Confirm message'
                        defaultMessage='Are you sure you want to'
                    />
                    <Space />
                    <span className='alert-message--danger'>
                        <FormattedMessage
                            id='oe.userGroups.deleteThisUserGroup'
                            description='Confirm message'
                            defaultMessage='Delete this User Group'
                        />
                    </span>
                    ?
                </span>
            ),
            confirmButtonProps: {
                text: (
                    <FormattedMessage
                        id='oe.userGroups.deleteUserGroup'
                        description='label text'
                        defaultMessage='Delete User Group'
                    />
                ),
                modifier: 'confirm-warn',
                handler: this.deleteGroupApiCall
            },
            cancelButtonProps: {
                modifier: 'cancel'
            }
        });
    };

    private completeGroupDeletion = (userGroupId: number) => {
        this.fetchUserGroupsList();
        this.closeEditGroupForm(userGroupId);
    };

    private deleteGroupApiCall = async () => {
        try {
            await this.userGroupsDAO.deleteGroup({ userGroupId: this.userGroupId, accountId: this.accountId });
            this.trackingService.get().track(TRACKING_EVENTS.DELETE_USER_GROUP, {
                'Name': this.groupFormDataVM.name
            });
            this.completeGroupDeletion(this.userGroupId);
        } catch (error) {
            alertServerError('Error', this.errorParsingService.extractMessage(error));
        }
    };

    public static parseSearchQuery(search: string = '', searchMin = SEARCH_TERM_MIN_LENGTH): string {
        const query = search.trim();

        return query.length > searchMin ? query : '';
    }

    @debounceExecution(500)
    public searchUsers(query: string) {
        const search = RoomFormVM.parseSearchQuery(query);
        this.fetchUsersList(search);
    }

    // Check this functionality
    @mobx.action
    // eslint-disable-next-line no-unused-vars
    public fetchUsersList = async (query: string = '') => {
        try {
            const response = await this.usersDAO.getAccountUsers({ accountId: this.accountId, activeOnly: true});
            this.onFetchUsersListSuccess(response);
        } catch (error) {
            if (error.response?.status !== 403) {
                this.onFetchUsersListError(error);
            }
        }
    };

    @mobx.action
    private onFetchUsersListSuccess(users: IUserDetailsDTO[]): void {
        const formattedResponse = users.map(user => ({ ...user, name: `${user.firstName} ${user.lastName}` }));

        this.usersList = formattedResponse;
    }

    @mobx.action
    private onFetchUsersListError(error): void {
        this.alertsService.alert({
            message: this.errorParsingService.extractMessage(error)
        });
    }
}

export { UserGroupsFormVM };
